import * as React from 'react';
import Box from '@mui/material/Box';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarProps,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Button,
  CircularProgress,
  debounce,
  LinearProgress,
} from '@mui/material';
import HeaderElipse from './headerElipse';
import RowButton from './rowButton';
import DropDown from './dropDown';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useDispatch, useSelector } from '../../state';
import {
  setSearchInput,
  searchPurchaseOrders,
  setLoading,
} from '../../state/purchaseOrderBrowseSlice';
import type { PurchaseOrderSearchInput } from '@repo/api-contract';
import { topNavBarHeight } from '../NavBars/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { openPurchaseOrderModal } from '../../state/purchaseOrderModalSlice';
import dayjs from 'dayjs';
import {
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'browse-purchase-orders' satisfies DataGridTableId;

const PURCHASE_ORDER_TABLE_COL_I18N =
  'team.Components.PurchaseOrderBrowseTable';
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar(
  props: GridToolbarProps & { selectedRows?: GridRowSelectionModel },
) {
  const { selectedRows = [] } = props;
  const dispatch = useDispatch();
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tCO } = useTranslation([], {
    keyPrefix: 'team.Components.PurchaseOrderBrowseTable',
  });
  const { searchInput, searchBy, loading } = useSelector(
    (store) => store.purchaseOrderBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: PurchaseOrderSearchInput = {
        query: searchQuery,
        searchBy,
        size: 100,
        page: 0,
      };
      dispatch(searchPurchaseOrders(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <DropDown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tCO('Search for Purchase Orders...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
            </React.Fragment>
          ),
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          const payload: PurchaseOrderSearchInput = {
            query: searchInput,
            searchBy,
            size: 100,
            page: 0,
          };
          dispatch(searchPurchaseOrders(payload));
        }}
      >
        {tGB('Search')}
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <button
        className="text-blue-600 border-0 bg-inherit hover:cursor-pointer"
        onClick={() => {
          dispatch(openPurchaseOrderModal({ resetState: true }));
        }}
      >
        {tGB('+ ADD')}
      </button>
      <GridToolbarExport
        slotProps={{
          tooltip: { title: 'Export data' },
        }}
      />
      <HeaderElipse selectedRows={selectedRows} />
    </GridToolbarContainer>
  );
}

export default function DataTables() {
  const dispatch = useDispatch();
  const { results, loading, searchBy } = useSelector(
    (store) => store.purchaseOrderBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);
  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const initialResults = async () => {
    if (team === 'init' || team === 'loading') return null;
    const payload: PurchaseOrderSearchInput = {
      query: '',
      searchBy,
      size: 100,
      page: 0,
    };
    await dispatch(searchPurchaseOrders(payload));
  };

  React.useEffect(() => {
    initialResults();
  }, []);
  const baseColumns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'code',
        type: 'string',
        flex: 1,
        headerName: 'Purchase Order No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Purchase Order No."
            params={params}
            keyPrefix={PURCHASE_ORDER_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'vendorName',
        flex: 1,
        headerName: 'Vendor Name',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Name"
            params={params}
            keyPrefix={PURCHASE_ORDER_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'customerDeposit',
        flex: 1,
        headerName: 'Customer Deposit',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Deposit"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'rate',
        flex: 1,
        headerName: 'Rate',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Rate"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{`${params.value * 100}%`}</span>
        ),
      },
      {
        field: 'createdAt',
        flex: 1,
        headerName: 'Date Created',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Date Created"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => {
          if (!params.value) return '';
          return dayjs(params.value).format('MM/DD/YYYY');
        },
      },
      {
        field: 'action',
        headerName: '',
        width: 55,
        resizable: false,
        sortable: false,
        renderHeader: () => null,
        renderCell: (params) => {
          return <RowButton order={params.row.order}></RowButton>;
        },
      },
    ];
  }, []);

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <div style={{ height: `calc(100vh - ${topNavBarHeight} - 250px)` }}>
      <DataGridPro
        apiRef={apiRef}
        columnHeaderHeight={56}
        sx={{
          border: 'none',
          padding: '1rem',
          '.MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#ECECEC;',
          },
          '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
          '.MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            whiteSpace: 'normal',
            lineHeight: 1.2,
          },
        }}
        rows={
          results
            ? results.results.map((result) => {
                return {
                  id: result.PurchaseOrderId,
                  code: result.code,
                  vendorName: result.vendorName,
                  customerDeposit: result.customerDeposit,
                  rate: result.rate,
                  createdAt: result.createdAt,
                  order: result,
                };
              })
            : []
        }
        loading={loading}
        columns={columns}
        columnVisibilityModel={persistedPrefs.columnVisibilityModel}
        onColumnVisibilityModelChange={
          persistedPrefs.onColumnVisibilityModelChange
        }
        onColumnWidthChange={persistedPrefs.onColumnWidthChange}
        onColumnOrderChange={persistedPrefs.onColumnOrderChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 100,
            },
          },
        }}
        pinnedColumns={{
          right: ['action'],
          left: [GRID_CHECKBOX_SELECTION_FIELD],
        }}
        pageSizeOptions={[1]}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnFilter
        autoHeight={false}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        slots={{
          loadingOverlay: Loader,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { selectedRows: rowSelectionModel },
        }}
      />
    </div>
  );
}
